import { useMemo, useEffect, useState, useSyncExternalStore } from 'react';
import { getSession } from './cloud';
import { initialState } from './domain';
import { configured, supabase } from './supabase';
import { WorkspaceStore, type WorkspaceUser } from './workspace';

export function useWorkspace(temporary = false) {
  const [store] = useState(
    () => new WorkspaceStore(configured, temporary ? initialState() : undefined),
  );
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const setState = useMemo(() => store.setter(snapshot.generation), [store, snapshot.generation]);

  const cloudActions = useMemo(
    () => ({
      reconcileCloud: (state: import('../types').AppState, generation: number) =>
        store.reconcileCloud(state, generation),
      hydrateCloud: (state: import('../types').AppState, generation: number, revision: number) =>
        store.hydrateCloud(state, generation, revision),
      recoverCloud: (state: import('../types').AppState, generation: number, revision: number) =>
        store.recoverCloud(state, generation, revision),
      acknowledgeCloud: (state: import('../types').AppState, generation: number) =>
        store.acknowledgeCloud(state, generation),
    }),
    [store],
  );

  useEffect(() => {
    if (temporary || !configured || !supabase) return;
    let active = true;
    let eventReceived = false;
    const userFrom = (user: { id: string; email?: string } | undefined): WorkspaceUser | null =>
      user ? { id: user.id, email: user.email ?? '' } : null;
    const subscription = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      eventReceived = true;
      store.switchUser(userFrom(session?.user));
    });
    void getSession()
      .then((session) => {
        if (active && !eventReceived) store.switchUser(userFrom(session?.user));
      })
      .catch((error) => {
        if (active && !eventReceived)
          store.authFailure(
            `Loginstatus kunne ikke kontrolleres. ${error instanceof Error ? error.message : 'Prøv at genindlæse siden.'}`,
          );
      });
    return () => {
      active = false;
      subscription.data.subscription.unsubscribe();
    };
  }, [store, temporary]);

  useEffect(() => {
    if (!snapshot.authReady || snapshot.storageError || snapshot.saveStatus !== 'saving') return;
    const timer = window.setTimeout(() => store.persist(snapshot.generation), 500);
    return () => window.clearTimeout(timer);
  }, [store, snapshot]);

  return {
    generation: snapshot.generation,
    revision: snapshot.revision,
    cloudDirty: snapshot.cloudDirty,
    recoveredDirty: snapshot.recoveredDirty,
    cloudConflict: snapshot.cloudConflict,
    ...cloudActions,
    state: snapshot.state,
    setState,
    saveStatus: snapshot.saveStatus,
    storageError: snapshot.storageError,
    user: snapshot.user,
    authReady: snapshot.authReady,
  };
}
